import React, { useMemo, useState } from "react";
import styles from "./card.module.scss";
import HoverCard from "./HoverCard";
import { LordIcon } from "@src/components/lord-icon/lord-icon";
import { IconInfo } from "@src/constants";

import PDFIcon from "../assets/pdfIcon.png";
import PPTIcon from '../assets/pptIcon.png';
import CALIcon from '../assets/calendarIcon.png'

type Props = {
  titleLine1: string;
  titleLine2?: string;
  mainIcon?: React.ReactNode;
  backGroundUrl?: string;
  imageUrl?: string;
  isImagePadding?: boolean
  isImageLargePadding?: boolean;
  isComingSoon?: boolean;
  isNew?: boolean;
  isPdf?: boolean;
  isPptIcon?: boolean;
  isCalendarIcon?: boolean;
  isAdmin?: boolean;
  distAdminIcon?: boolean;
  adminIcon?: boolean;
  section?: string;
  hoverTitle?: React.ReactNode;
  hoverHint?: string;
  hoverReleaseDate?: string;
  marginBottom?: boolean;
  isSummary?: boolean;
  openCard?: () => void;
  isTrial?: boolean;
  isResilent?: boolean;
  className?: string;
  subTitleClassName?: string;
  isUsageGraph?: boolean;
  isQuizTiles?: boolean;
};

function MainCard(props: Props) {
  const {
    titleLine1,
    titleLine2,
    mainIcon,
    imageUrl,
    isImagePadding = false,
    isImageLargePadding = false,
    backGroundUrl,
    isComingSoon,
    isPdf,
    isAdmin,
    distAdminIcon,
    adminIcon,
    section,
    hoverTitle,
    hoverHint,
    hoverReleaseDate,
    marginBottom,
    openCard,
    isPptIcon,
    isCalendarIcon,
    isSummary,
    isTrial,
    isNew,
    isResilent,
    className='',
    subTitleClassName='',
    isUsageGraph=false,
    isQuizTiles
  } = props;

  const isClassRoom = section === "Classroom";
  const [hover, setHover] = useState(false);
  const Icon = isPdf ? PDFIcon : isPptIcon ? PPTIcon : isCalendarIcon ? CALIcon : null

  const imageViewStyle = useMemo(() => {
    const classes = [styles.imageView];
    
    if (isClassRoom) {
      classes.push(styles.classroomMargin);
    }
    classes.push(className)

    return classes.join(' ');
  }, [isClassRoom, className])

  const imageStyle = useMemo(() => {
    const classes = [''];
    if ( isSummary ) {
      classes.push(styles.summaryImageCard)
    } else {
      if (isUsageGraph) {
        classes.push(styles.imageGraphCard)
      } else {
        classes.push(styles.imageCard)
      }
    }
    if ( isComingSoon) {
      classes.push(styles.comingSoonImage)
    }
    if (!isSummary && isQuizTiles) {
      classes.push(styles.quizImage)
    }
    if (isCalendarIcon) {
      classes.push(styles.shadowTile)
    }

    return classes.join(' ');
  }, [isComingSoon, isSummary, isCalendarIcon, isUsageGraph, isQuizTiles])

  const cardTitleStyles = useMemo(() => {
    const classes = [styles.cardTitle];
    if (isQuizTiles) {
      classes.push(styles.quizFont)
    }
    return classes.join(' ');
  }, [isQuizTiles])

  return (
    <div
      className={styles.mainCard}
      onClick={openCard}
      style={{ cursor: isComingSoon ? "default" : "pointer", pointerEvents: isComingSoon ? 'none': 'auto', opacity: isTrial ? '0.5' : '1' }}
    >
      <div
        className={styles.background}
        style={{ backgroundImage: `url(${backGroundUrl})` }}
      ></div>
      <div className={styles.cardView}>
        <div
          className={`${cardTitleStyles} ${
            marginBottom && styles.marginBottom
          }`}
          style={{ width: isClassRoom ? "46%" : "58%" }}
        >
          {mainIcon}
          <h2
            style={{
              fontFamily: isResilent ? 'TragicMarker' : '',
              fontSize: isClassRoom ? "22px" : "26px",
              color: isComingSoon ? "#A0A0A0" : "#274649",
            }}
            /* This is safe because this is not taking user input so not an injection risk,
               but it's necessary to render the <br/> element. */
            dangerouslySetInnerHTML={{ __html: titleLine1 }}
          ></h2>
          <span
            className={subTitleClassName}
            style={{
              color: isComingSoon
                ? "#A0A0A0"
                : isAdmin && section === "Admin"
                ? "#FFB073"
                : "#274649",
            }}
          >
            {titleLine2}
          </span>
          {isComingSoon && (
            <div className={styles.comingsoonLabel}>Coming Soon</div>
          )}
          {isNew && (
            <div className={styles.comingsoonLabel}>New</div>
          )}
          {Icon && (
            <div>
              <img className={styles.pdfIcon} src={Icon} alt="Icon" />
            </div>
          )}
          {adminIcon && <div className={styles.adminLabel}>ADMINISTRATOR</div>}
          {distAdminIcon && <div className={styles.adminLabel}>DISTRICT ADMINISTRATOR</div>}
        </div>
        {imageUrl && (
          <div
            style={{ marginTop: isPptIcon ? '100px' : isUsageGraph ? '100px' : '0px', marginRight: isImagePadding ? '10px' : isImageLargePadding ? '20px' : '0px' }}
            className={imageViewStyle}
          >
            <img
              src={imageUrl}
              alt="Play"
              className={imageStyle}
            />
          </div>
        )}
      </div>
      {hoverTitle && (
        <HoverCard
          title={hoverTitle || ""}
          hint={hoverHint || ""}
          releaseDate={hoverReleaseDate || ""}
          isActive={hover}
          openCard={openCard}
        />
      )}

      {section !== "Profile" && (
        <div
          className={styles.iconWrapper}
          onMouseOver={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <LordIcon
            src={IconInfo}
            isHover={true}
            colors={{ primary: "#274649", secondary: "#274649" }}
            size={26}
          />
        </div>
      )}
    </div>
  );
}

export default MainCard;
